import { AxiomAnalytics } from "./forwarders/axiom-analytics";
import { PostHogAnalytics } from "./forwarders/posthog-analytics";
import type {
	AnalyticsConfig,
	AnalyticsEventType,
	AnalyticsMetadata,
	AnalyticsProvider,
	EventProperties,
	IAnalyticsForwarder,
	RegisteredProperties,
	TrackClickOptions,
	TrackFocusOptions,
	TrackInputOptions,
} from "./types";

type AnalyticsForwarderFactories = {
	posthog: (config: NonNullable<AnalyticsConfig["posthog"]>) => IAnalyticsForwarder;
	axiom: (config: NonNullable<AnalyticsConfig["axiom"]>) => IAnalyticsForwarder;
};

const DEFAULT_FORWARDER_FACTORIES: AnalyticsForwarderFactories = {
	posthog: (config) =>
		new PostHogAnalytics(config.apiKey, config.host, {
			browserOptions: config.browserOptions,
			nodeOptions: config.nodeOptions,
		}),
	axiom: (config) => new AxiomAnalytics(config.token, config.orgId, config.dataset, config.environment, config.project),
};

const assertNever = (provider: never): never => {
	throw new Error(`Unsupported analytics provider: ${provider}`);
};

export class AnalyticsHandler {
	private forwarders: IAnalyticsForwarder[];
	private providers: readonly AnalyticsProvider[];

	constructor(config: AnalyticsConfig, forwarderFactories: AnalyticsForwarderFactories = DEFAULT_FORWARDER_FACTORIES) {
		this.forwarders = [];
		this.providers = [];

		this.providers = config.providers;

		for (const provider of config.providers) {
			switch (provider) {
				case "posthog":
					if (config.posthog) {
						this.forwarders.push(forwarderFactories.posthog(config.posthog));
					}
					break;
				case "axiom":
					if (config.axiom) {
						this.forwarders.push(forwarderFactories.axiom(config.axiom));
					}
					break;
				default:
					assertNever(provider);
			}
		}
	}

	track<T extends AnalyticsEventType>(event: T, properties: EventProperties<T>, metadata?: AnalyticsMetadata): void {
		for (const forwarder of this.forwarders) {
			forwarder.track(event, properties, metadata);
		}
	}

	trackClick(options?: TrackClickOptions): void {
		this.track(
			"Click",
			{
				label: options?.label,
				elementType: options?.elementType,
				value: options?.value,
			},
			options?.metadata,
		);
	}

	trackFocus(options?: TrackFocusOptions): void {
		this.track(
			"Focus",
			{
				label: options?.label,
				elementType: options?.elementType,
			},
			options?.metadata,
		);
	}

	trackInput(options?: TrackInputOptions): void {
		this.track(
			"Input",
			{
				label: options?.label,
				elementType: options?.elementType,
			},
			options?.metadata,
		);
	}

	trackPageView(
		page: string,
		options?: {
			title?: string;
			referrer?: string;
			path?: string;
			url?: string;
			metadata?: AnalyticsMetadata;
		},
	): void {
		this.track(
			"PageView",
			{
				page,
				title: options?.title,
				referrer: options?.referrer,
				path: options?.path,
				url: options?.url,
			},
			options?.metadata,
		);
	}

	identify(): void {
		for (const forwarder of this.forwarders) {
			forwarder.identify();
		}
	}

	async flush(): Promise<void> {
		await Promise.all(this.forwarders.map((forwarder) => forwarder.flush()));
	}

	getProviders(): readonly AnalyticsProvider[] {
		return this.providers;
	}

	register(properties: RegisteredProperties): void {
		for (const forwarder of this.forwarders) {
			forwarder.register(properties);
		}
	}

	startSessionRecording(): void {
		for (const forwarder of this.forwarders) {
			forwarder.startSessionRecording();
		}
	}
}
